import React, { Suspense, useEffect, useMemo } from "react";
import { MediaCategoriesSkeleton } from "./media-categories-skeleton/MediaCategoriesSkeleton";
import { baseUrl, fetchKmsData } from "@kaltura/mediaspace-shared-utils";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { KmsTypePublishEntryCategories } from "@kaltura/mediaspace-shared-types";
import { PublishedCategories } from "./published-categories/PublishedCategories";
import { ExternalPublishedCategories } from "./external-published-categories/ExternalPublishedCategories";
import styled from "@emotion/styled";
import { CategoriesListVariant } from "./categories-list/CategoriesList";

export interface MediaCategoriesInnerProps {
    variant: CategoriesListVariant;
    fetchPublishLocations: () => {
        entryCategories?: KmsTypePublishEntryCategories;
    };
    onDataLoaded?: () => void;
}

/**
 * Media Categories content
 */
function MediaCategories(props: MediaCategoriesInnerProps) {
    const { variant, fetchPublishLocations, onDataLoaded } = props;
    const locations = fetchPublishLocations();

    useEffect(() => {
        onDataLoaded?.();
    }, [onDataLoaded]);

    const { entryCategories } = locations;

    return (
        <StyledMediaCategoriesContainer className="kms-ds-publish-media-categories">
            {entryCategories && <PublishedCategories variant={variant} entryCategories={entryCategories} />}
            {!!entryCategories?.externalCategories?.length && (
                <ExternalPublishedCategories
                    variant={variant}
                    externalCategories={entryCategories?.externalCategories}
                />
            )}
        </StyledMediaCategoriesContainer>
    );
}

export interface MediaCategoriesContainerProps {
    variant: CategoriesListVariant;
    entryId: string;
    showExternal?: boolean;
    onDataLoaded?: () => void;
}

/**
 * Media Categories suspense container - fetch the media publish locations,
 * and renders them on the page (private and unlisted modals, my media items)
 */
export const MediaCategoriesContainer = (props: MediaCategoriesContainerProps) => {
    const { variant, entryId, showExternal = false, onDataLoaded } = props;

    // fetch data from kms
    const fetchPublishLocations = useMemo(
        () =>
            fetchKmsData<{ entryCategories?: KmsTypePublishEntryCategories }>(
                baseUrl + "/publishmedia/media/get-categories/",
                {
                    entryId: entryId,
                    showExternal: showExternal,
                }
            ),
        [entryId, showExternal]
    );

    // render with Suspense
    return (
        <ErrorBoundary fallback={<MediaCategoriesSkeleton />}>
            <Suspense fallback={<MediaCategoriesSkeleton />}>
                <MediaCategories
                    variant={variant}
                    fetchPublishLocations={fetchPublishLocations}
                    onDataLoaded={onDataLoaded}
                />
            </Suspense>
        </ErrorBoundary>
    );
};

const StyledMediaCategoriesContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(1),
    "& > *:last-child": {
        marginBottom: 0,
    },
}));
